import {
  BusinessLogFilter,
  ClassifierMappingAutoComplete,
  DATE_FORMAT,
  DatePicker,
  DirectoryFilter,
  DirectoryFilterProps,
  getDayjs,
  getIsoDateFromFormattedDate,
  GroupDescriptionFilterInput,
  PageRequestParams,
} from '@atlas-frontend/sdk';
import { RefObject, useMemo } from 'react';
import { FormInstance } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { ClassifierFilterInput } from './ClassifierFilterInput';
import { BusinessLogTypeFilterInput } from './BusinessLogTypeFilterInput';
import translations from './BusinessLogFilterPane.translations.json';
import { DescriptionTypeFilterInput } from './DescriptionTypeFilterInput';
import { Dayjs } from 'dayjs';
import { DataSubjectFilterInput } from './DataSubjectFilterInput';

const { addTranslations, useTranslation } = i18nNsFactory(
  'business-log-filter-pane',
);
addTranslations(translations);

export interface BusinessLogFilterProps extends DirectoryFilterProps {
  filters: BusinessLogFilter & PageRequestParams;
  formRef: RefObject<FormInstance | null>;
}

export function BusinessLogFilterPane(props: BusinessLogFilterProps) {
  const { t } = useTranslation();

  const filterFields = useMemo(
    () => [
      {
        name: 'type',
        label: t('field.type.label'),
        initialValue: props.filters.type,
        input: () => <BusinessLogTypeFilterInput />,
      },
      {
        name: 'descriptionType',
        label: t('field.descriptionType.label'),
        initialValue: props.filters.descriptionType,
        input: () => <DescriptionTypeFilterInput formRef={props.formRef} />,
      },
      {
        name: 'description',
        label: t('field.description.label'),
        initialValue: props.filters.description,
        input: () => (
          <GroupDescriptionFilterInput
            formRef={props.formRef}
            selectedDescription={props.filters.description}
          />
        ),
      },
      {
        name: 'logDataValueDate',
        label: t('field.logDataValueDate.label'),
        initialValue: props.filters.logDataValueDate?.map(
          (v) => v && getDayjs(getIsoDateFromFormattedDate(v))!,
        ) as [Dayjs | null, Dayjs | null],
        input: () => (
          <DatePicker.RangePicker
            id="logDataValueDate-filter"
            allowEmpty={[true, true]}
            format={DATE_FORMAT}
            allowClear
            style={{ width: '100%' }}
          />
        ),
      },
      {
        name: 'logDataSubject',
        label: t('field.logDataSubject.label'),
        initialValue: props.filters.logDataSubject,
        input: () => (
          <DataSubjectFilterInput
            selectedSubject={props.filters.logDataSubject}
          />
        ),
      },
      {
        name: 'classifier',
        label: t('field.classifier.label'),
        initialValue: props.filters.classifier,
        input: () => (
          <ClassifierFilterInput
            selectedClassifier={props.filters.classifier}
          />
        ),
      },
      {
        name: 'mapping',
        label: t('field.mapping.label'),
        initialValue: props.filters.mapping,
        input: () => (
          <ClassifierMappingAutoComplete
            id="mapping-filter"
            selectedMapping={props.filters.mapping}
          />
        ),
      },
    ],
    [props.filters, props.formRef, t],
  );

  return (
    <DirectoryFilter
      {...props}
      fields={filterFields}
      onClear={() => {
        props.formRef.current?.setFieldsValue({
          classifier: '',
          description: '',
          descriptionType: '',
          mapping: '',
          type: [],
          logDataValueDate: [null, null],
        });
      }}
      ref={props.formRef}
    />
  );
}
